import { FC, useMemo, useState, useEffect, useCallback } from "react";
import { Box, Flex, Text } from "@radix-ui/themes";
import styled from "@emotion/styled";
import { useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { SidebarSection } from "@/app/layout/sidebar-section";
import { SidebarGroupId, SidebarSectionId, SidebarSectionItem } from "@/app/layout/types";
import { localStorageModule } from "@/modules/local-storage";
import { SIDEBAR_PADDING, SIDEBAR_WIDTH } from "@/app/layout/constants";
import { buildSidebarSections } from "@/app/layout/sidebar-config";
import { api } from "@/modules/api";
import { extractListData } from "@/modules/api/response-helpers";
import { toastModule } from "@/modules/toast";
import { BaseButton } from "@/components/button";
import { Copy } from "lucide-react";

interface SidebarProps {
  onNavigate?: () => void;
}

const sectionCollapseKey = (sectionId: SidebarSectionId) => `section:${sectionId}`;
const groupCollapseKey = (groupId: SidebarGroupId) => `group:${groupId}`;

const DEFAULT_COLLAPSED_STATE: Record<string, boolean> = {
  [sectionCollapseKey(SidebarSectionId.Overview)]: false,
  [sectionCollapseKey(SidebarSectionId.Company)]: true,
  [sectionCollapseKey(SidebarSectionId.Support)]: true,
  [sectionCollapseKey(SidebarSectionId.Sync)]: true,
  [sectionCollapseKey(SidebarSectionId.Utilities)]: true,
  [sectionCollapseKey(SidebarSectionId.Admin)]: true,
  [groupCollapseKey(SidebarGroupId.UtilitiesWebClipper)]: true,
  [groupCollapseKey(SidebarGroupId.AdminBadges)]: true,
};

export const Sidebar: FC<SidebarProps> = ({ onNavigate }) => {
  const { pathname } = useLocation();
  const [collapsedState, setCollapsedState] =
    useState<Record<string, boolean>>(DEFAULT_COLLAPSED_STATE);

  // Load collapsed state from local storage on mount
  useEffect(() => {
    const loadCollapsedState = async () => {
      const savedState = await localStorageModule.readSidebarState();
      // Merge saved state with defaults, preferring saved values
      setCollapsedState({
        ...DEFAULT_COLLAPSED_STATE,
        ...savedState,
      });
    };
    loadCollapsedState();
  }, []);

  // Save collapsed state to local storage whenever it changes
  useEffect(() => {
    localStorageModule.writeSidebarState(collapsedState);
  }, [collapsedState]);

  const getCollapsedValue = (key: string) =>
    collapsedState[key] ?? DEFAULT_COLLAPSED_STATE[key] ?? false;

  const toggleCollapsedKey = (key: string) => {
    setCollapsedState(prev => ({
      ...prev,
      [key]: !(prev[key] ?? DEFAULT_COLLAPSED_STATE[key] ?? false),
    }));
  };

  const isSectionCollapsed = (sectionId: SidebarSectionId) =>
    getCollapsedValue(sectionCollapseKey(sectionId));

  const toggleSection = (sectionId: SidebarSectionId) => {
    toggleCollapsedKey(sectionCollapseKey(sectionId));
  };

  const isGroupCollapsed = (groupId: SidebarGroupId) =>
    getCollapsedValue(groupCollapseKey(groupId));

  const toggleGroup = (groupId: SidebarGroupId) => {
    toggleCollapsedKey(groupCollapseKey(groupId));
  };

  const accountQuery = useQuery({
    queryKey: ["account", "me"],
    queryFn: () => api.get("/v1/me/account", {}),
  });

  const spacesQuery = useQuery({
    queryKey: ["spaces", "me"],
    queryFn: () => api.get("/v1/me/spaces", {}),
  });

  const spaceAccountsQuery = useQuery({
    queryKey: ["space-accounts", "me"],
    queryFn: () => api.get("/v1/me/space-accounts", {}),
  });

  const primarySpaceAccountId = useMemo(() => {
    const spacesResponse = spacesQuery.data;
    const spaceAccountsResponse = spaceAccountsQuery.data;

    if (!spacesResponse?.data || !spaceAccountsResponse?.data) {
      return null;
    }

    const spaces = extractListData<{ id: string; is_primary_space?: boolean }>(spacesResponse.data);
    const spaceAccounts = extractListData<{ id: string; space_id?: string | null }>(
      spaceAccountsResponse.data
    );

    if (!spaces.length || !spaceAccounts.length) {
      return null;
    }

    const primarySpace = spaces.find(space => space.is_primary_space);
    if (!primarySpace) {
      return null;
    }

    const primarySpaceAccount = spaceAccounts.find(
      spaceAccount => spaceAccount.space_id === primarySpace.id
    );

    return primarySpaceAccount?.id ?? null;
  }, [spacesQuery.data, spaceAccountsQuery.data]);

  const accountData = accountQuery.data?.data as
    | {
        id?: string;
        profile_display_name?: string | null;
        profile_email_address?: string | null;
        email_address?: string | null;
        email?: string | null;
      }
    | undefined;

  const accountDisplayName =
    accountData?.profile_display_name ?? accountData?.profile_email_address ?? "";
  const accountEmail =
    accountData?.profile_email_address ?? accountData?.email_address ?? accountData?.email ?? "";
  const accountId = accountData?.id ?? null;

  const sidebarItems = useMemo<SidebarSectionItem[]>(
    () => buildSidebarSections(pathname),
    [pathname]
  );

  const SidebarContent = () => (
    <SidebarFlex>
      {sidebarItems.map(section => (
        <SidebarSection
          key={section.id}
          section={section}
          isCollapsed={isSectionCollapsed(section.id)}
          onToggle={() => toggleSection(section.id)}
          onItemSelect={onNavigate}
          isGroupCollapsed={isGroupCollapsed}
          onGroupToggle={toggleGroup}
        />
      ))}
      <SidebarAccountBox>
        <AccountName title={accountDisplayName || undefined}>
          {accountDisplayName || (accountQuery.isLoading ? "..." : "Account")}
        </AccountName>
        <AccountEmail title={accountEmail || undefined}>
          {accountEmail || (accountQuery.isLoading ? "" : "Email unavailable")}
        </AccountEmail>
        <CopyGroup>
          <CopyRow>
            <CopyLabel>Account ID</CopyLabel>
            <CopyValueButton label="Account ID" value={accountId} />
          </CopyRow>
          <CopyRow>
            <CopyLabel>Primary Space Account ID</CopyLabel>
            <CopyValueButton label="Primary Space Account ID" value={primarySpaceAccountId} />
          </CopyRow>
        </CopyGroup>
      </SidebarAccountBox>
    </SidebarFlex>
  );

  return (
    <SidebarBox>
      <SidebarContent />
    </SidebarBox>
  );
};

const SidebarBox = styled(Box)`
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  height: 100%;
  position: relative;
  padding-bottom: 3.5px;
  padding-left: ${SIDEBAR_PADDING}px;
  padding-right: ${SIDEBAR_PADDING}px;
  padding-top: 24px;
  box-sizing: border-box;
  border-top-right-radius: 4px;
  z-index: 1;
  font-weight: 500;
  overflow-y: auto;
  overflow-x: hidden;
  align-self: flex-start;
  background-color: var(--gray-1);
  text-rendering: optimizelegibility;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

const SidebarFlex = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;
  height: 100%;
`;

const SidebarAccountBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background-color: var(--gray-1);
  border-radius: 6px;
  flex-grow: 1;
  margin-bottom: 16px;
`;

const AccountName = styled(Text)`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  color: var(--gray-12);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 4px;
  height: 20px;
`;

const AccountEmail = styled(Text)`
  margin-top: -5px;
  font-size: 11px;
  color: var(--gray-11);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 16px;
`;

const CopyGroup = styled(Flex)`
  flex-direction: column;
`;

const CopyRow = styled(Flex)`
  flex-direction: column;
`;

const CopyLabel = styled(Text)`
  font-size: 7px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--gray-11);
`;

const CopyButtonStyled = styled(BaseButton)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-start;
  font-size: 9px;
  color: var(--gray-12);
  border: 1px solid var(--gray-4);
  background-color: var(--gray-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  margin-top: -5px;

  &:hover {
    background-color: var(--gray-3);
  }

  &:disabled,
  &[data-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CopyValueText = styled.span`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: var(
    --font-family-mono,
    "SFMono-Regular",
    Consolas,
    "Liberation Mono",
    Menlo,
    monospace
  );
`;

interface CopyValueButtonProps {
  label: string;
  value?: string | null;
}

const CopyValueButton: FC<CopyValueButtonProps> = ({ label, value }) => {
  const handleCopy = useCallback(async () => {
    if (!value) {
      return;
    }

    if (typeof navigator === "undefined" || !navigator.clipboard) {
      toastModule.triggerToast({ content: `Clipboard not available for ${label}.` });
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toastModule.triggerToast({ content: `${label} copied.` });
    } catch (_err) {
      toastModule.triggerToast({ content: `Failed to copy ${label}.` });
    }
  }, [label, value]);

  return (
    <CopyButtonStyled
      type="button"
      onClick={handleCopy}
      disabled={!value}
      aria-label={value ? `Copy ${label}` : `${label} unavailable`}
      data-disabled={!value}
    >
      <Copy size={12} />
      <CopyValueText title={value ?? "Unavailable"}>{value ?? "Unavailable"}</CopyValueText>
    </CopyButtonStyled>
  );
};
